const Parse = global.Parse;
import { Observation } from 'domain';
import CommonUtils from 'utils/CommonUtils';

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_PAGE = 1;

const get = (observationId) => {
    const query = new Parse.Query(Observation)
        .include([
            'name',
            'name.taxonomy',
            'name.taxonomy.primarySystem',
            'name.taxonomy.subject',
            'substantiation',
            'locDeterminization',
            'substrate',
            'quality',
            'location',
            'location.parent',
            'location.parent.parent',
            'observedBy',
            'reviewedBy',
            'event',
            'event.type',
            'town',
            'keyword'
        ]);
 
    return {
        type: 'GET_OBSERVATION',
        meta: { observationId },
        payload: query.get(observationId).then(async (observation) => {
            if (observation.get('location')) {
                const locationNotesQuery = new Parse.Query('Note')
                    .include(['type'])
                    .equalTo('location', observation.get('location'));
                const locationNotes = await locationNotesQuery.find();
                observation.set('locationNotes', locationNotes);
            }
            return observation;
        })
    };
};

const list = (filters = {}) => {
    const query = new Parse.Query(Observation)
        .include([
            'name',
            'name.taxonomy',
            'name.taxonomy.primarySystem',
            'name.taxonomy.subject',
            'substantiation',
            'locDeterminization',
            'substrate',
            'quality',
            'location',
            'location.parent',
            'location.parent.parent',
            'observedBy',
            'reviewedBy',
            'event',
            'event.type',
            'town',
            'keyword'
        ]);
    
    const page = filters.page || 1;
    const pageSize = filters.pageSize || DEFAULT_PAGE_SIZE;
    const loadMore = filters.loadMore || false;
    
    if (filters.names && filters.names.length > 0) {
        const nameQueries = filters.names.map(name => {
            const nameQuery = new Parse.Query('Name');
            return nameQuery.equalTo('objectId', name.id);
        });
        const nameQuery = Parse.Query.or(...nameQueries);
        query.matchesQuery('name', nameQuery);
    }

    if (filters.event) {
        query.equalTo('event', filters.event);
    }

    if (filters.location) {
        query.equalTo('location', filters.location);
    }

    const countQuery = new Parse.Query(Observation);
    
    if (filters.names && filters.names.length > 0) {
        const nameQueries = filters.names.map(name => {
            const nameQuery = new Parse.Query('Name');
            return nameQuery.equalTo('objectId', name.id);
        });
        const nameQuery = Parse.Query.or(...nameQueries);
        countQuery.matchesQuery('name', nameQuery);
    }

    if (filters.event) {
        countQuery.equalTo('event', filters.event);
    }

    if (filters.location) {
        countQuery.equalTo('location', filters.location);
    }
    
    query.skip((page - 1) * pageSize);
    query.limit(pageSize);

    return {
        type: loadMore ? 'LOAD_MORE_OBSERVATION' : 'LIST_OBSERVATION',
        meta: { filters, page, pageSize },
        payload: Promise.all([
            countQuery.count(),
            query.find().then(async observations => {
                const observationsWithEvidence = await Promise.all(observations.map(async observation => {
                    const evidenceQuery = new Parse.Query('ObservationEvidence')
                        .equalTo('observation', observation)
                        .include(['evidence', 'evidence.sourceOwned', 'evidence.repository']);
                    const observationEvidence = await evidenceQuery.find();
                    const evidence = observationEvidence.map(oe => oe.get('evidence'));
                    observation.set('evidence', evidence);
                    
                    if (observation.get('location')) {
                        const locationNotesQuery = new Parse.Query('Note')
                            .include(['type'])
                            .equalTo('location', observation.get('location'));
                        const locationNotes = await locationNotesQuery.find();
                        observation.set('locationNotes', locationNotes);
                    }
                    
                    return observation;
                }));
                return observationsWithEvidence;
            })
        ]).then(([totalCount, observations]) => {
            return {
                data: observations,
                pagination: {
                    total: totalCount,
                    page,
                    pageSize,
                    hasMore: (page * pageSize) < totalCount
                }
            };
        })
    };
};

const clearList = () => ({
    type: 'LIST_OBSERVATION',
    payload: Promise.resolve([])
});

const clearCurrent = () => ({
    type: 'CLEAR_OBSERVATION_CURRENT'
});


const save = (observation) => {
    const observationToSave = new Observation();
    
    if (observation.id) {
        observationToSave.id = observation.id;
    } else {
    }

    observationToSave.set('name', CommonUtils.createPointer('Name', observation.name));
    if (observation.substantiation) {
        observationToSave.set('substantiation', CommonUtils.createPointer('Substantiation', observation.substantiation));
    }
    if (observation.location) {
        observationToSave.set('location', CommonUtils.createPointer('Location', observation.location));
    }
    observationToSave.set('observedBy', CommonUtils.createPointer('Person', observation.observedBy));
    if (observation.town) {
        observationToSave.set('town', CommonUtils.createPointer('Town', observation.town));
    }
    observationToSave.set('date', observation.date);
    observationToSave.set('isDetected', observation.isDetected);
    observationToSave.set('flag', observation.flag);
    observationToSave.set('observationRestriction', observation.observationRestriction);
    
    if (observation.dateSpecificity) {
        observationToSave.set('dateSpecificity', observation.dateSpecificity);
    }
    
    if (observation.locDeterminization) {
        observationToSave.set('locDeterminization', CommonUtils.createPointer('LocDeterminization', observation.locDeterminization));
    }
    
    if (observation.substrate) {
        observationToSave.set('substrate', CommonUtils.createPointer('Substrate', observation.substrate));
    }
    
    if (observation.quality) {
        observationToSave.set('quality', CommonUtils.createPointer('ObservationQuality', observation.quality));
    }
    
    if (observation.reviewedBy) {
        observationToSave.set('reviewedBy', CommonUtils.createPointer('Person', observation.reviewedBy));
    }
    
    if (observation.lat) {
        observationToSave.set('lat', Math.round(observation.lat * 10000) / 10000);
    }
    if (observation.lng) {
        observationToSave.set('lng', Math.round(observation.lng * 10000) / 10000);
    }
    
    if (observation.event) {
        observationToSave.set('event', { 
            __type: 'Pointer', 
            className: 'Event', 
            objectId: observation.event 
        });
    }

    if (observation.keyword) {
        observationToSave.set('keyword', { 
            __type: 'Pointer', 
            className: 'Keyword', 
            objectId: observation.keyword 
        });
    }

    return {
        type: 'SAVE_OBSERVATION',
        payload: observationToSave.save()
    };
};

const runQuery = (criteria = [], page = DEFAULT_PAGE, pageSize = DEFAULT_PAGE_SIZE, loadMore = false) => {
    const countQuery = createQueryFromCriteria(criteria);
    const fetchQuery = createQueryFromCriteria(criteria);
    
    fetchQuery.skip((page - 1) * pageSize);
    fetchQuery.limit(pageSize);
    
    fetchQuery.include([
        'name',
        'name.taxonomy',
        'name.taxonomy.primarySystem',
        'name.taxonomy.subject',
        'substantiation',
        'locDeterminization',
        'substrate',
        'quality',
        'location',
        'location.parent',
        'location.parent.parent',
        'observedBy',
        'reviewedBy',
        'event',
        'event.type',
        'town',
        'keyword'
    ]);

    return {
        type: loadMore ? 'LOAD_MORE_OBSERVATION' : 'LIST_OBSERVATION',
        meta: { criteria, page, pageSize, loadMore },
        payload: Promise.all([
            countQuery.count(),
            fetchQuery.find().then(async observations => {
                const observationsWithEvidence = await Promise.all(observations.map(async observation => {
                    const evidenceQuery = new Parse.Query('ObservationEvidence')
                        .equalTo('observation', observation)
                        .include(['evidence', 'evidence.sourceOwned', 'evidence.repository']);
                    const observationEvidence = await evidenceQuery.find();
                    const evidence = observationEvidence.map(oe => oe.get('evidence'));
                    observation.set('evidence', evidence);
                    
                    if (observation.get('location')) {
                        const locationNotesQuery = new Parse.Query('Note')
                            .include(['type'])
                            .equalTo('location', observation.get('location'));
                        const locationNotes = await locationNotesQuery.find();
                        observation.set('locationNotes', locationNotes);
                    }
                    
                    return observation;
                }));
                return observationsWithEvidence;
            })
        ]).then(([totalCount, observations]) => {
            return {
                data: observations,
                pagination: {
                    total: totalCount,
                    page,
                    pageSize,
                    hasMore: (page * pageSize) < totalCount
                }
            };
        })
    };
};

const applyCondition = (query, criterion, fieldSchema) => {
    const { field, operator, value } = criterion;
    
    if (fieldSchema.type.type === 'Pointer') {
        const pointerValue = CommonUtils.createPointer(fieldSchema.type.targetClass, value.id);
        
        switch (operator) {
            case 'equals':
                query.equalTo(field, pointerValue);
                break;
            case 'not equals':
                query.notEqualTo(field, pointerValue);
                break;
        }
    } else {
        switch (operator) {
            case 'equals':
                if (fieldSchema.type === 'Date') {
                    query.equalTo(field, new Date(value));
                } else {
                    query.equalTo(field, value);
                }
                break;
            case 'not equals':
                if (fieldSchema.type === 'Date') {
                    query.notEqualTo(field, new Date(value));
                } else {
                    query.notEqualTo(field, value);
                }
                break;
            case 'contains':
                if (fieldSchema.type === 'String') {
                    query.matches(field, new RegExp(value, 'i'));
                }
                break;
            case 'greater than':
                if (fieldSchema.type === 'Number') {
                    query.greaterThan(field, Number(value));
                }
                break;
            case 'less than':
                if (fieldSchema.type === 'Number') {
                    query.lessThan(field, Number(value));
                }
                break;
            case 'before':
                if (fieldSchema.type === 'Date') {
                    query.lessThan(field, new Date(value));
                }
                break;
            case 'after':
                if (fieldSchema.type === 'Date') {
                    query.greaterThan(field, new Date(value));
                }
                break;
            case 'between':
                if (Array.isArray(value) && value.length === 2) {
                    if (fieldSchema.type === 'Date') {
                        query.greaterThan(field, new Date(value[0]));
                        query.lessThan(field, new Date(value[1]));
                    } else if (fieldSchema.type === 'Number') {
                        query.greaterThan(field, Number(value[0]));
                        query.lessThan(field, Number(value[1]));
                    }
                }
                break;
        }
    }
};


const createQueryFromCriteria = (criteria = []) => {
    const criteriaByField = criteria.reduce((acc, criterion) => {
        const { field } = criterion;
        if (field === 'name') {
            if (!acc['name.taxonomy']) {
                acc['name.taxonomy'] = [];
            }
            acc['name.taxonomy'].push({
                ...criterion,
                field: 'name.taxonomy',
                value: criterion.value.get('taxonomy')
            });

        } else {
            if (!acc[field]) {
                acc[field] = [];
            }
            acc[field].push(criterion);
        }
        return acc;
    }, {});
    
    let mainQuery = new Parse.Query(Observation);
    
    Object.entries(criteriaByField).forEach(([field, fieldCriteria]) => {
        if (field === 'name.taxonomy') {
            if (fieldCriteria.length === 1) {
                const criterion = fieldCriteria[0];
                const taxonomyPointer = CommonUtils.createPointer('Taxonomy', criterion.value.id);
                const nameQuery = new Parse.Query('Name');
                nameQuery.equalTo('taxonomy', taxonomyPointer);
                mainQuery.matchesQuery('name', nameQuery);
            } else if (fieldCriteria.length > 1) {
                const nameQueries = fieldCriteria.map(criterion => {
                    const taxonomyPointer = CommonUtils.createPointer('Taxonomy', criterion.value.id);
                    const nameQuery = new Parse.Query('Name');
                    nameQuery.equalTo('taxonomy', taxonomyPointer);
                    return nameQuery;
                });
                const combinedNameQuery = Parse.Query.or(...nameQueries);
                mainQuery.matchesQuery('name', combinedNameQuery);
            }
        } else {
            const fieldSchema = Observation.FIELDS.find(f => f.field === field);
            
            if (!fieldSchema) return;
            
            if (fieldCriteria.length === 1) {
                applyCondition(mainQuery, fieldCriteria[0], fieldSchema);
            } 
            else if (fieldCriteria.length > 1) {
                const subQueries = fieldCriteria.map(criterion => {
                    const subQuery = new Parse.Query(Observation);
                    applyCondition(subQuery, criterion, fieldSchema);
                    return subQuery;
                });
                
                mainQuery = Parse.Query.or(...subQueries);
            }
        }
    });
    
    return mainQuery;
};

const loadMoreObservations = (criteria, currentPage, pageSize) => {
    return runQuery(criteria, currentPage + 1, pageSize, true);
};

const listObservationsForEvidence = (evidenceId, page = DEFAULT_PAGE, pageSize = DEFAULT_PAGE_SIZE, loadMore = false) => {
    const evidencePointer = CommonUtils.createPointer('Evidence', evidenceId);
    const observationEvidenceQuery = new Parse.Query('ObservationEvidence')
        .equalTo('evidence', evidencePointer)
        .include('observation');
    
    return {
        type: loadMore ? 'LOAD_MORE_OBSERVATION' : 'LIST_OBSERVATION',
        meta: { evidenceId, page, pageSize },
        payload: observationEvidenceQuery.find().then(async observationEvidenceRecords => {
            const observationPointers = observationEvidenceRecords.map(oe => oe.get('observation'));
            
            if (observationPointers.length === 0) {
                return {
                    data: [],
                    pagination: {
                        total: 0,
                        page,
                        pageSize,
                        hasMore: false
                    }
                };
            }
            
            const countQuery = new Parse.Query(Observation)
                .containedIn('objectId', observationPointers.map(op => op.id));
                
            const query = new Parse.Query(Observation)
                .containedIn('objectId', observationPointers.map(op => op.id))
                .include([
                    'name',
                    'name.taxonomy',
                    'name.taxonomy.primarySystem',
                    'name.taxonomy.subject',
                    'substantiation',
                    'locDeterminization',
                    'substrate',
                    'quality',
                    'location',
                    'location.parent',
                    'location.parent.parent',
                    'observedBy',
                    'reviewedBy',
                    'event',
                    'event.type',
                    'town',
                    'keyword'
                ]);
            
            query.skip((page - 1) * pageSize);
            query.limit(pageSize);
            
            const [totalCount, observations] = await Promise.all([
                countQuery.count(),
                query.find()
            ]);
            
            const observationsWithEvidence = await Promise.all(observations.map(async observation => {
                const evidenceQuery = new Parse.Query('ObservationEvidence')
                    .equalTo('observation', observation)
                    .include(['evidence', 'evidence.sourceOwned', 'evidence.repository']);
                const observationEvidence = await evidenceQuery.find();
                const evidence = observationEvidence.map(oe => oe.get('evidence'));
                observation.set('evidence', evidence);
                
                if (observation.get('location')) {
                    const locationNotesQuery = new Parse.Query('Note')
                        .include(['type'])
                        .equalTo('location', observation.get('location'));
                    const locationNotes = await locationNotesQuery.find();
                    observation.set('locationNotes', locationNotes);
                }
                
                return observation;
            }));
            
            return {
                data: observationsWithEvidence,
                pagination: {
                    total: totalCount,
                    page,
                    pageSize,
                    hasMore: (page * pageSize) < totalCount
                }
            };
        })
    };
};

const deleteObservation = (observationId) => {
    return {
        type: 'DELETE_OBSERVATION',
        payload: (async () => {
            const observationPointer = CommonUtils.createPointer('Observation', observationId);
            
            const observationEvidenceQuery = new Parse.Query('ObservationEvidence');
            observationEvidenceQuery.equalTo('observation', observationPointer);
            const observationEvidences = await observationEvidenceQuery.find();
            
            const noteQuery = new Parse.Query('Note');
            noteQuery.equalTo('observation', observationPointer);
            const notes = await noteQuery.find();
            
            if (observationEvidences.length > 0) {
                await Parse.Object.destroyAll(observationEvidences);
            }
            if (notes.length > 0) {
                await Parse.Object.destroyAll(notes);
            }
            
            const observationQuery = new Parse.Query('Observation');
            const observation = await observationQuery.get(observationId);
            await observation.destroy();
            
            return {
                observationId,
                deletedObservationEvidencesCount: observationEvidences.length,
                deletedNotesCount: notes.length
            };
        })()
    };
};

export default {
    get,
    list,
    save,
    runQuery,
    clearList,
    clearCurrent,
    listObservationsForEvidence,
    loadMoreObservations,
    deleteObservation
};